/**
 * The "Solutions We Offer" block: a two-part heading, a paragraph and a call-to-action button,
 * then a row of linked image cards ending in a "more" card. It is the live version of this
 * section; `components/Shared/SolutionsWeOffer/` is an older one that nothing renders. Despite the
 * folder name, nothing here uses parallax scrolling.
 *
 * Below `lg` it renders `./Mobile/SolutionsWeOfferMobile.tsx`, which has no heading: the caller
 * supplies one, for example the `components/Shared/AllSections/AllSections.tsx` accordion tab or
 * the heading `components/HomePage/homepage.tsx` draws above it. From `lg` up it renders
 * `./Desktop/SolutionsWeOfferDesktop.tsx`. Both draw their cards with `./Shared/SolutionsCard.tsx`.
 *
 * The Ryerson "Customized Metal Solutions" copy is `./Content.tsx` → `SolutionsContent`: the
 * default when `content` is absent (`components/HomePage/homepage.tsx`,
 * `components/MetalSolutions/MetalSolutionsLandingRyerson.tsx`), and passed explicitly by
 * `src/pages/metal-solutions/industries/index.tsx`. With other `content` it is a generic card
 * section, for example the "Who Do We Help" section of the pages under
 * `src/pages/metal-solutions/solutions/` and of the bar, tube and plate capability pages, and the
 * commitment cards in `components/Shared/RyersonCommitment/RyersonCommitment.tsx`.
 * {@link SolutionsWeOfferContent} is the type of that copy in
 * `src/content/ryerson/metal-solutions/**` and `src/content/sts/**`.
 *
 * The Southern Tool Steel variants in `./STS/` copy these types and views rather than extending
 * them.
 */
import React from "react";
import SolutionsWeOfferDesktop from "./Desktop/SolutionsWeOfferDesktop";
import SolutionsWeOfferMobile from "./Mobile/SolutionsWeOfferMobile";
import { IconList } from "@ryerson/frontend.assets";
import { Media } from "@ryerson/frontend.next";
import { SolutionsContent } from "./Content";
import { useApplication } from "@ryerson/frontend.application";

/** One card in the row. */
export type CardsContent = {
	/** Required by the type, but the views here don't render it. */
	icon: keyof IconList;
	/**
	 * Card caption. The card renders it in an `h5` without `white-space: pre-line`, so a `\n`
	 * shows as a space.
	 */
	text: string;
	/** The card's background image, a site path such as `/images/...`. */
	backgroundUrl: string;
	/** Where the card links. Without it the desktop card links to `#`, the mobile card to `""`. */
	link?: string;
};

/** One language's copy for the section. */
export type SolutionsWeOfferContent = {
	/** First half of the desktop heading. */
	title1: string;
	/** Second half of the desktop heading, in dark grey. */
	title2: string;
	/** The paragraph beside the button (desktop) or above the cards (mobile). */
	description: string;
	buttonLabel: string;
	cards: CardsContent[];
	/**
	 * Section name, not rendered here. Pages list the section in `AllSections` under it, which
	 * makes it the mobile accordion header and, slugified, the `#` anchor.
	 */
	title: string;
	/** Caption of the last ("more") card, which always uses a fixed background image. */
	actionLabel: string;
	/** Where the last ("more") card links. */
	actionUrl: string;
	/** Where the button goes. Without it the button calls `navigate("")`. */
	buttonUrl?: string;
};

/**
 * Props of {@link SolutionsWeOffer}. `background` is the section's theme background (default
 * `"tertiary"`). `bottomBackground` (default `"primary"`) colours the band under the desktop card
 * row: `"secondary"` gives the secondary background, any other value white. Mobile ignores it.
 */
export interface SolutionsWeOfferProps {
	content?: SolutionsWeOfferContent;
	background?: "primary" | "secondary" | "tertiary";
	bottomBackground?: "primary" | "secondary" | "tertiary";
}

/** Props the wrapper passes to the desktop and mobile views, with every value resolved. */
export interface SolutionsWeOfferComponentProps {
	content: SolutionsWeOfferContent;
	background: "primary" | "secondary" | "tertiary";
	bottomBackground: "primary" | "secondary" | "tertiary";
}

/** The "Solutions We Offer" section; see the file header. */
const SolutionsWeOffer: React.FC<SolutionsWeOfferProps> = ({
	background = "tertiary",
	content,
	bottomBackground = "primary",
}) => {
	const {
		localization: { language },
	} = useApplication();
	if (!content) {
		content = SolutionsContent[language];
	}
	return (
		<>
			<Media lessThan="lg">
				<SolutionsWeOfferMobile
					content={content}
					background={background}
					bottomBackground={bottomBackground}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<SolutionsWeOfferDesktop
					content={content}
					background={background}
					bottomBackground={bottomBackground}
				/>
			</Media>
		</>
	);
};

export default SolutionsWeOffer;
